import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createClient } from '@deepgram/sdk';

const DeepgramVoiceInput = ({ onVoiceInput, onTranscriptUpdate, isDarkMode, language = 'en' }) => {
  const [isListening, setIsListening] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [isBrowserSupported, setIsBrowserSupported] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [audioLevel, setAudioLevel] = useState(0);
  const [isConnecting, setIsConnecting] = useState(false);
  
  const deepgramClientRef = useRef(null);
  const deepgramConnectionRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const timeoutRef = useRef(null);
  const silenceTimeoutRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  const currentTranscriptRef = useRef(''); // Add ref to track current transcript
  const connectionStatusRef = useRef('disconnected'); // Add ref to track connection status for callbacks
  const isSmallMobile = useRef(window.innerWidth < 375);
  const isMobile = useRef(window.innerWidth < 768);
  
  // Track window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      isMobile.current = window.innerWidth < 768;
      isSmallMobile.current = window.innerWidth < 375;
    };
    
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Cleanup function for stopping current session
  const cleanup = useCallback((fullCleanup = false) => {
    // Clear timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = null;
    }

    // Stop audio visualization
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // Close audio context only on full cleanup
    if (audioContextRef.current && fullCleanup) {
      audioContextRef.current.close().catch(console.error);
      audioContextRef.current = null;
    }

    // Stop media recorder
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try {
        mediaRecorderRef.current.stop();
      } catch (error) {
        console.error('Error stopping media recorder:', error);
      }
      mediaRecorderRef.current = null;
    }

    // Stop media stream only on full cleanup (component unmount)
    if (streamRef.current && fullCleanup) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    // Close Deepgram connection
    if (deepgramConnectionRef.current) {
      try {
        deepgramConnectionRef.current.finish();
      } catch (error) {
        console.error('Error closing Deepgram connection:', error);
      }
      deepgramConnectionRef.current = null;
    }

    // Reset states
    setConnectionStatus('disconnected');
    connectionStatusRef.current = 'disconnected'; // Update ref too
    setAudioLevel(0);
    setTranscript('');
    currentTranscriptRef.current = ''; // Also reset the ref
  }, []);

  // Get or create microphone stream
  const getMicrophoneStream = useCallback(async () => {
    // If we already have a stream and it's active, reuse it
    if (streamRef.current && streamRef.current.active) {
      return streamRef.current;
    }

    // Request new stream - this will trigger permission popup on first use
    const stream = await navigator.mediaDevices.getUserMedia({ 
      audio: {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      } 
    });
    
    streamRef.current = stream;
    return stream;
  }, []);

  // Initialize Deepgram client
  useEffect(() => {
    const apiKey = process.env.REACT_APP_DEEPGRAM_API_KEY;
    if (!apiKey) {
      setErrorMessage('Deepgram API key not found');
      setIsBrowserSupported(false);
      return;
    }

    try {
      deepgramClientRef.current = createClient(apiKey);
      setIsBrowserSupported(true);
    } catch (error) {
      console.error('Error initializing Deepgram client:', error);
      setErrorMessage('Failed to initialize Deepgram client');
      setIsBrowserSupported(false);
    }
  }, []);

  // Clean up on component unmount
  useEffect(() => {
    return () => {
      cleanup(true); // Full cleanup on unmount
    };
  }, [cleanup]);

  // Send transcript updates to parent component
  useEffect(() => {
    if (onTranscriptUpdate && transcript) {
      onTranscriptUpdate(transcript);
    }
  }, [transcript, onTranscriptUpdate]);

  // Audio visualization
  const startAudioVisualization = useCallback((stream) => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    
    const audioContext = audioContextRef.current;
    const analyser = audioContext.createAnalyser();
    const source = audioContext.createMediaStreamSource(stream);
    
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.8;
    source.connect(analyser);
    
    analyserRef.current = analyser;
    
    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    
    const updateAudioLevel = () => {
      if (analyserRef.current && isListening) {
        analyser.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length;
        setAudioLevel(average / 255);
        animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
      }
    };
    
    updateAudioLevel();
  }, [isListening]);

  const getLanguageCode = useCallback(() => {
    switch (language) {
      case 'ar': return 'ar';
      case 'sr': return 'sr';
      case 'ru': return 'ru';
      case 'es': return 'es';
      case 'fr': return 'fr';
      case 'de': return 'de';
      case 'it': return 'it';
      case 'pt': return 'pt';
      case 'zh': return 'zh';
      case 'ja': return 'ja';
      case 'ko': return 'ko';
      default: return 'en';
    }
  }, [language]);

  const stopListening = useCallback(() => {
    // Immediately reset all visual states first
    setIsListening(false);
    setAudioLevel(0);
    setConnectionStatus('disconnected');
    connectionStatusRef.current = 'disconnected'; // Update ref too
    
    // Then cleanup connections and streams
    cleanup(false); // Partial cleanup - keep stream alive
    
    // No need to send transcript here since it's already being sent in real-time
    
    // Reset processing state after a short delay
    setTimeout(() => setIsProcessing(false), 1500);
  }, [cleanup]);

  const startListening = useCallback(async () => {
    if (!deepgramClientRef.current || !isBrowserSupported || isListening || isConnecting) {
      return;
    }

    setIsConnecting(true);
    setErrorMessage('');
    setIsListening(false);

    try {
      // Get microphone stream (reuse existing if available)
      const stream = await getMicrophoneStream();

      // Start audio visualization
      startAudioVisualization(stream);

      // Create Deepgram live transcription connection with compatible parameters
      const connection = deepgramClientRef.current.listen.live({
        model: 'nova-2',
        language: getLanguageCode(),
        smart_format: true,
        interim_results: true,
        endpointing: 300,
        punctuate: true
      });

      deepgramConnectionRef.current = connection;

      // Handle connection events
      connection.on('open', () => {
        setConnectionStatus('connected');
        connectionStatusRef.current = 'connected'; // Update ref too
        setIsListening(true);
        setIsConnecting(false);
        setErrorMessage('');
        setTranscript('');
        currentTranscriptRef.current = ''; // Reset transcript ref
        
        // Start sending audio data with compatible format
        let mimeType = 'audio/webm;codecs=opus';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'audio/webm';
          if (!MediaRecorder.isTypeSupported(mimeType)) {
            mimeType = 'audio/mp4';
            if (!MediaRecorder.isTypeSupported(mimeType)) {
              mimeType = ''; // Use default
            }
          }
        }
        
        const mediaRecorderOptions = mimeType ? {
          mimeType: mimeType
        } : {}; // Removed audioBitsPerSecond which might cause issues
        
        const mediaRecorder = new MediaRecorder(stream, mediaRecorderOptions);
        
        mediaRecorderRef.current = mediaRecorder;
        
        mediaRecorder.onerror = (event) => {
          console.error('MediaRecorder error:', event);
        };
        
        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0 && deepgramConnectionRef.current && connectionStatusRef.current === 'connected') {
            try {
              deepgramConnectionRef.current.send(event.data);
            } catch (error) {
              console.error('Error sending audio to Deepgram:', error);
            }
          }
        };
        
        mediaRecorder.onstart = () => {
          // MediaRecorder started
        };
        
        mediaRecorder.onstop = () => {
          // MediaRecorder stopped
        };
        
        mediaRecorder.start(250); // Increased to 250ms for more stable chunks
      });

      connection.on('Results', (data) => {
        // Try multiple possible data structures
        let transcriptText = '';
        
        if (data?.channel?.alternatives?.[0]?.transcript) {
          transcriptText = data.channel.alternatives[0].transcript;
        } else if (data?.alternatives?.[0]?.transcript) {
          transcriptText = data.alternatives[0].transcript;
        } else if (data?.transcript) {
          transcriptText = data.transcript;
        }
        
        if (transcriptText && transcriptText.trim()) {
          setTranscript(transcriptText);
          currentTranscriptRef.current = transcriptText; // Update ref as well
          
          // Send interim results to input box in real-time
          onVoiceInput(transcriptText.trim());
          
          // Reset silence timeout
          if (silenceTimeoutRef.current) {
            clearTimeout(silenceTimeoutRef.current);
          }
          
          // Set silence timeout for final result
          if (data.is_final) {
            silenceTimeoutRef.current = setTimeout(() => {
              if (transcriptText.trim()) {
                setIsProcessing(true);
                // Final transcript is already sent above, just stop listening
                stopListening();
              }
            }, 2000); // 2 seconds of silence after final result
          }
        }
      });

      // Also keep the transcript event handler in case that's what's used
      connection.on('transcript', (data) => {
        const transcriptText = data.channel?.alternatives?.[0]?.transcript || data.alternatives?.[0]?.transcript || data.transcript;
        
        if (transcriptText && transcriptText.trim()) {
          setTranscript(transcriptText);
          currentTranscriptRef.current = transcriptText; // Update ref as well
          
          // Send interim results to input box in real-time
          onVoiceInput(transcriptText.trim());
          
          // Reset silence timeout
          if (silenceTimeoutRef.current) {
            clearTimeout(silenceTimeoutRef.current);
          }
          
          // Set silence timeout for final result
          if (data.is_final) {
            silenceTimeoutRef.current = setTimeout(() => {
              if (transcriptText.trim()) {
                setIsProcessing(true);
                // Final transcript is already sent above, just stop listening
                stopListening();
              }
            }, 2000); // 2 seconds of silence after final result
          }
        }
      });
      
      connection.on('error', (error) => {
        console.error('Deepgram connection error:', error);
        setErrorMessage('Connection error occurred');
        setIsConnecting(false);
        stopListening();
      });

      connection.on('close', () => {
        setConnectionStatus('disconnected');
        connectionStatusRef.current = 'disconnected'; // Update ref too
        setIsConnecting(false);
        
        // Stop listening when connection closes and update button state
        setIsListening(false);
        setAudioLevel(0);
        
        // Clean up MediaRecorder if it's still running
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
          try {
            mediaRecorderRef.current.stop();
          } catch (error) {
            console.error('Error stopping MediaRecorder on connection close:', error);
          }
        }
      });

      // Set overall timeout (30 seconds max)
      timeoutRef.current = setTimeout(() => {
        if (isListening) {
          // Transcript is already being sent in real-time, just stop listening
          stopListening();
        }
      }, 30000);

    } catch (error) {
      console.error('Error starting Deepgram listening:', error);
      setIsConnecting(false);
      
      if (error.name === 'NotAllowedError') {
        setErrorMessage('Microphone access denied. Please allow microphone access.');
      } else if (error.name === 'NotFoundError') {
        setErrorMessage('No microphone found. Please check your device.');
      } else {
        setErrorMessage('Failed to start voice recognition. Please try again.');
      }
      setIsListening(false);
    }
  }, [isBrowserSupported, getLanguageCode, onVoiceInput, transcript, isListening, isConnecting, startAudioVisualization, getMicrophoneStream, stopListening]);

  const toggleListening = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      setErrorMessage('');
      startListening();
    }
  }, [isListening, stopListening, startListening]);

  // Get button size to match ChatInput buttons (48px like upload button)
  const getButtonSize = () => {
    return '48px'; // Match upload button size (w-12 h-12)
  };

  // Get icon size to match other buttons
  const getIconSize = () => {
    return 'text-[16px]'; // Match upload button icon size
  };

  return (
    <div className="relative">
      {/* Error message */}
      <AnimatePresence>
        {errorMessage && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.9 }}
            transition={{ duration: 0.2 }}
            className="absolute bottom-full mb-3 left-1/2 transform -translate-x-1/2 w-max max-w-[280px] text-center z-50"
          >
            <div 
              className={`text-xs p-3 rounded-xl shadow-lg border ${
                isDarkMode 
                  ? 'bg-red-900/90 text-red-100 border-red-800' 
                  : 'bg-red-50 text-red-800 border-red-200'
              }`}
            >
              <i className="fas fa-exclamation-triangle mr-2"></i>
              {errorMessage}
            </div>
            {/* Error bubble arrow */}
            <div 
              className="w-2 h-2 transform rotate-45 mt-[-4px] mx-auto"
              style={{
                backgroundColor: isDarkMode ? 'rgb(127, 29, 29)' : 'rgb(254, 242, 242)',
                borderRight: isDarkMode ? '1px solid rgb(153, 27, 27)' : '1px solid rgb(254, 202, 202)',
                borderBottom: isDarkMode ? '1px solid rgb(153, 27, 27)' : '1px solid rgb(254, 202, 202)'
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Speech transcript bubble */}
      <AnimatePresence>
        {(isListening || isConnecting) && transcript && (
          <motion.div
            initial={{ opacity: 0, y: 10, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: 10, height: 0 }}
            transition={{ duration: 0.3 }}
            className="absolute bottom-full mb-3 left-1/2 transform -translate-x-1/2 z-40"
            style={{
              width: isSmallMobile.current ? '240px' : isMobile.current ? '280px' : '320px',
              maxWidth: 'calc(100vw - 40px)',
            }}
          >
            <div 
              className={`p-4 rounded-xl shadow-xl border backdrop-blur-sm ${
                isDarkMode 
                  ? 'bg-gray-800/95 text-white border-gray-600/50' 
                  : 'bg-white/95 text-gray-800 border-gray-200'
              }`}
            >
              {/* Header with connection status */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div 
                    className={`w-2 h-2 rounded-full ${
                      connectionStatus === 'connected' ? 'bg-green-500' : 
                      isConnecting ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                  />
                  <span className="text-xs font-medium opacity-70">
                    {isConnecting ? 'Connecting...' : `Deepgram ${connectionStatus}`}
                  </span>
                </div>
                {isListening && (
                  <div className="flex items-center gap-1">
                    <div className="flex gap-0.5">
                      {[...Array(4)].map((_, i) => (
                        <motion.div
                          key={i}
                          className={`w-0.5 bg-green-500 rounded-full`}
                          animate={{
                            height: [2, 8 + audioLevel * 12, 2],
                          }}
                          transition={{
                            duration: 0.5 + i * 0.1,
                            repeat: Infinity,
                            ease: "easeInOut"
                          }}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Transcript text */}
              <div 
                className="max-h-24 overflow-y-auto break-words leading-relaxed"
                style={{ 
                  scrollbarWidth: 'none',
                  fontSize: isSmallMobile.current ? '13px' : '14px',
                }}
              >
                {transcript || (isConnecting ? 'Initializing...' : 'Listening...')}
              </div>
            </div>
            
            {/* Speech bubble arrow */}
            <div 
              className="w-3 h-3 transform rotate-45 mt-[-6px] mx-auto"
              style={{
                backgroundColor: isDarkMode ? 'rgb(31, 41, 55)' : 'white',
                borderRight: isDarkMode ? '1px solid rgba(75, 85, 99, 0.5)' : '1px solid rgb(229, 231, 235)',
                borderBottom: isDarkMode ? '1px solid rgba(75, 85, 99, 0.5)' : '1px solid rgb(229, 231, 235)'
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Voice recording button */}
      <motion.button
        onClick={toggleListening}
        disabled={!isBrowserSupported || isProcessing}
        whileHover={{ scale: isBrowserSupported && !isProcessing ? 1.02 : 1 }}
        whileTap={{ scale: isBrowserSupported && !isProcessing ? 0.98 : 1 }}
        className={`relative flex items-center justify-center rounded-full transition-colors ${
          !isBrowserSupported || isProcessing 
            ? 'opacity-50 cursor-not-allowed' 
            : ''
        } ${
          isListening 
            ? 'bg-red-500 hover:bg-red-600' 
            : isDarkMode 
              ? 'bg-[#222222] hover:bg-gray-700' 
              : 'bg-gray-100 hover:bg-gray-200'
        }`}
        style={{ 
          width: getButtonSize(),
          height: getButtonSize(),
        }}
        title={
          isConnecting ? 'Connecting to Deepgram...' :
          isListening ? "Stop listening (Deepgram)" : 
          "Start voice input (Deepgram)"
        }
      >
        {/* Microphone icon */}
        <motion.div 
          animate={isListening ? {
            scale: [1, 1.1, 1],
            transition: { 
              repeat: Infinity, 
              duration: 1.5,
              repeatType: "reverse" 
            }
          } : { scale: 1 }}
          className="relative z-10"
        >
          <i 
            className={`fas ${
              isConnecting ? 'fa-spinner fa-spin' :
              isProcessing ? 'fa-cog fa-spin' : 
              isListening ? 'fa-stop' : 'fa-microphone'
            } ${getIconSize()}`}
            style={{
              color: isListening 
                ? 'white' 
                : isDarkMode ? '#9CA3AF' : '#6B7280' // Match upload button colors
            }}
          />
        </motion.div>

        {/* Animated rings - only when actively listening */}
        <AnimatePresence key={`rings-${isListening}-${connectionStatus}`}>
          {isListening && connectionStatus === 'connected' && (
            <>
              {/* Inner ring */}
              <motion.div
                className="absolute inset-0 rounded-full border-2"
                initial={{ scale: 1, opacity: 0 }}
                animate={{ 
                  scale: [1, 1.5, 1.8], 
                  opacity: [0.6, 0.3, 0] 
                }}
                exit={{ opacity: 0, scale: 1 }}
                transition={{ 
                  repeat: Infinity, 
                  duration: 2,
                  ease: "easeOut" 
                }}
                style={{
                  borderColor: 'rgba(239, 68, 68, 0.6)',
                }}
              />
              
              {/* Outer ring */}
              <motion.div
                className="absolute inset-0 rounded-full border-2"
                initial={{ scale: 1, opacity: 0 }}
                animate={{ 
                  scale: [1, 2, 2.5], 
                  opacity: [0.4, 0.2, 0] 
                }}
                exit={{ opacity: 0, scale: 1 }}
                transition={{ 
                  repeat: Infinity, 
                  duration: 2.5, 
                  delay: 0.5,
                  ease: "easeOut" 
                }}
                style={{
                  borderColor: 'rgba(239, 68, 68, 0.4)',
                }}
              />
            </>
          )}
        </AnimatePresence>

        {/* Audio level indicator - only when actively listening with audio */}
        <AnimatePresence key={`audio-${isListening}-${connectionStatus}-${audioLevel}`}>
          {isListening && connectionStatus === 'connected' && audioLevel > 0.05 && (
            <motion.div
              className="absolute inset-0 rounded-full"
              initial={{ opacity: 0 }}
              animate={{
                boxShadow: `0 0 ${10 + audioLevel * 20}px rgba(239, 68, 68, ${0.3 + audioLevel * 0.4})`,
                opacity: 1
              }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.1 }}
            />
          )}
        </AnimatePresence>
      </motion.button>
    </div>
  );
};

export default DeepgramVoiceInput;
